import { AxiosResponse } from 'axios'
import { api } from 'API'
import {
  ITrainingPlan,
  ITrainingPlanDetail,
  ITrainingPlanWithRelations,
  ITrainingPlanSearchFilterDto,
  ICreateTrainingPlanDto,
  ICreateTrainingWeekDto,
  ICreateTrainingDayDto,
  ICreateTrainingBlockDto,
  ICreateTrainingStationDto,
  ITrainingDrillMetric
} from 'interfaces/training-plan'
import { ITrainingRx } from 'interfaces/training-rx'

const BASE = '/training-plans'

export async function searchTrainingPlans(
  filterDto: ITrainingPlanSearchFilterDto,
  orgId?: number
): Promise<{ results: ITrainingPlanWithRelations[]; totalCount: number }> {
  // orgId (query) scopes the call to one team, overriding the interceptor's
  // global active-org stamp. BE resolves org scope from `?orgId=`, not the body.
  const response: AxiosResponse = await api.post(
    `${BASE}/search`,
    filterDto,
    orgId != null ? { params: { orgId } } : undefined
  )
  return response.data
}

export async function createTrainingPlan(dto: ICreateTrainingPlanDto, orgId?: number): Promise<ITrainingPlan> {
  const response: AxiosResponse = await api.post(BASE, dto, orgId != null ? { params: { orgId } } : undefined)
  return response.data
}

export async function updateTrainingPlanById(id: number, data: Partial<ITrainingPlan>): Promise<void> {
  await api.patch(`${BASE}/${id}`, data)
}

export async function archiveMultipleTrainingPlans(ids: number[]): Promise<void> {
  await api.patch(`${BASE}/archive-multiple`, { ids })
}

export async function restoreMultipleTrainingPlans(ids: number[]): Promise<void> {
  await api.patch(`${BASE}/restore-multiple`, { ids })
}

export async function getTrainingPlanById(id: number): Promise<ITrainingPlan> {
  const response: AxiosResponse = await api.get(`${BASE}/${id}`)
  return response.data
}

export async function getTrainingPlanDetailById(id: number): Promise<ITrainingPlanDetail> {
  const response: AxiosResponse = await api.get(`${BASE}/${id}/detail`)
  return response.data
}

export async function createTrainingPlanVariant(
  id: number,
  name: string
): Promise<{ message: string; data: { id: number } }> {
  const response: AxiosResponse = await api.post(`${BASE}/${id}/variants`, { name })
  return response.data
}

export async function duplicateTrainingPlan(
  id: number,
  name: string
): Promise<{ message: string; data: { id: number } }> {
  const response: AxiosResponse = await api.post(`${BASE}/${id}/duplicate`, { name })
  return response.data
}

export async function createTrainingWeek(dto: ICreateTrainingWeekDto): Promise<void> {
  await api.post('/training-weeks', dto)
}

export async function updateTrainingWeek(id: number, data: Partial<ICreateTrainingWeekDto>): Promise<void> {
  await api.patch(`/training-weeks/${id}`, data)
}

export async function deleteTrainingWeek(id: number): Promise<void> {
  await api.delete(`/training-weeks/${id}`)
}

export async function createTrainingDay(dto: ICreateTrainingDayDto): Promise<void> {
  await api.post('/training-days', dto)
}

export async function updateTrainingDay(id: number, data: Partial<ICreateTrainingDayDto>): Promise<void> {
  await api.patch(`/training-days/${id}`, data)
}

export async function deleteTrainingDay(id: number): Promise<void> {
  await api.delete(`/training-days/${id}`)
}

export async function createTrainingBlock(dto: ICreateTrainingBlockDto): Promise<void> {
  await api.post('/training-blocks', dto)
}

export async function updateTrainingBlock(id: number, data: Partial<ICreateTrainingBlockDto>): Promise<void> {
  await api.patch(`/training-blocks/${id}`, data)
}

export async function updateTrainingBlockPositions(updates: { id: number; position: number }[]): Promise<void> {
  await api.patch('/training-blocks/batch', { updates })
}

export async function deleteTrainingBlock(id: number): Promise<void> {
  await api.delete(`/training-blocks/${id}`)
}

export async function createTrainingStation(dto: ICreateTrainingStationDto): Promise<void> {
  await api.post('/training-stations', dto)
}

export async function updateTrainingStation(id: number, data: Partial<ICreateTrainingStationDto>): Promise<void> {
  await api.patch(`/training-stations/${id}`, data)
}

export async function deleteTrainingStation(id: number): Promise<void> {
  await api.delete(`/training-stations/${id}`)
}

export async function addTrainingStationDrillsWithPositions(payload: {
  trainingStationId: number
  drillPositions: Array<{ drillId: number; position: number; isNew?: boolean }>
  existingDrillPositions?: Array<{ id: number; position: number }>
}): Promise<{
  stationId: number
  drills: Array<{ _id: number; drillId: number; name: string; position: number; trainingRx: ITrainingRx | null }>
}> {
  const response: AxiosResponse = await api.post('/training-station-drills/add-with-positions', payload)
  return response.data
}

export async function updateTrainingStationDrillPositions(payload: {
  trainingStationId: number
  drillPositions: Array<{ id: number; position: number }>
}): Promise<void> {
  await api.patch('/training-station-drills/positions/batch', payload)
}

export async function moveTrainingStationDrills(payload: {
  drillIds: number[]
  fromStationId: number
  toStationId: number
  newStationDrillPositions: Array<{ id: number; position: number }>
}): Promise<void> {
  await api.patch('/training-station-drills/move', payload)
}

export async function deleteTrainingStationDrill(id: number): Promise<void> {
  await api.delete(`/training-station-drills/${id}`)
}

export async function deleteTrainingStationDrills(trainingStationId: number, ids: number[]): Promise<void> {
  await api.patch('/training-station-drills/delete/batch', { trainingStationId, ids })
}

export async function duplicateTrainingWeek(
  weekId: number,
  targetPlanId: number,
  position: 'beginning' | 'end'
): Promise<any> {
  const response: AxiosResponse = await api.post(`/training-weeks/${weekId}/duplicate`, { targetPlanId, position })
  return response.data
}

export async function duplicateTrainingDay(
  dayId: number,
  targetWeekId: number,
  position: 'beginning' | 'end'
): Promise<any> {
  const response: AxiosResponse = await api.post(`/training-days/${dayId}/duplicate`, { targetWeekId, position })
  return response.data
}

export async function duplicateTrainingBlock(
  blockId: number,
  targetDayId: number,
  position: 'beginning' | 'end'
): Promise<any> {
  const response: AxiosResponse = await api.post(`/training-blocks/${blockId}/duplicate`, { targetDayId, position })
  return response.data
}

export async function duplicateTrainingStation(
  stationId: number,
  targetBlockId: number,
  position: 'beginning' | 'end'
): Promise<any> {
  const response: AxiosResponse = await api.post(`/training-stations/${stationId}/duplicate`, {
    targetBlockId,
    position
  })
  return response.data
}

export async function syncDrillMetrics(
  drillId: number,
  metrics: Array<{ metricId: number; required?: boolean }>
): Promise<ITrainingDrillMetric[]> {
  const response: AxiosResponse = await api.put(`/training-station-drills/${drillId}/metrics`, { metrics })
  return response.data
}

export async function getDrillMetrics(drillId: number): Promise<ITrainingDrillMetric[]> {
  const response: AxiosResponse = await api.get(`/training-station-drills/${drillId}/metrics`)
  return response.data
}
